import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  TouchableOpacity,
  ImageBackground,
  StyleSheet,
  useWindowDimensions,
  Platform,
  Text,
} from "react-native";
import { RouteProp, useRoute } from "@react-navigation/native";
import Carousel from "react-native-reanimated-carousel";
import { useDispatch, useSelector } from "react-redux";

import { RootStackParamList } from "../../navigator";
import { RootState } from "../../../store";
import { LeftArrowPng, RightArrowPng } from "../../../assets";
import { useDevice } from "../../../utils/hooks/useDevice";
import TournamentTable from "../LobbyTable/TournamentTable";
import { tournamentsActiveRequest } from "../../../store/tournaments/tournamentsActions";
import { TableItem } from "../../../store/types";

export type ActiveTournamentRoute = RouteProp<
  RootStackParamList,
  "ActiveTournament"
>;

export const ActiveTournamentsSection = () => {
  const dispatch = useDispatch();
  const { params } = useRoute<ActiveTournamentRoute>();

  const { width, height } = useWindowDimensions();
  const { isLandscape, isPortrait, isXLg, isLg } = useDevice();

  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 980;
  const isSmall = isLandscape && width < 980;

  const itemsPerPage =
    (isMobile && isPortrait) || (isTablet && isPortrait) ? 1 : 3;

  const [isClient, setIsClient] = useState(false);
  const carouselRef = useRef<any>(null);

  const roomId = String(params?.roomId ?? "");

  const activeState = useSelector(
    (state: RootState) => state.tournaments.activeByRoom?.[roomId],
  );

  const tournaments = activeState?.data ?? [];
  const loading = activeState?.loading ?? false;
  const error = activeState?.error ?? null;

  useEffect(() => {
    setIsClient(Platform.OS !== "web" ? true : typeof window !== "undefined");
  }, []);

  useEffect(() => {
    if (roomId) {
      dispatch(tournamentsActiveRequest({ roomId }));
    }
  }, [dispatch, roomId]);

  const pages: TableItem[][] = useMemo(() => {
    const out: TableItem[][] = [];
    for (let i = 0; i < tournaments.length; i += itemsPerPage) {
      out.push(tournaments.slice(i, i + itemsPerPage));
    }
    return out;
  }, [tournaments, itemsPerPage]);

  const handleNext = () => {
    carouselRef.current?.next();
  };

  const handlePrev = () => {
    carouselRef.current?.prev();
  };

  if (!isClient) return null;

  return (
    <View
      style={[
        styles.container,
        {
          marginTop: isSmall ? 0 : 40,
          justifyContent: "center",
          width: isPortrait ? width : width * 0.9,
          margin: "auto",
          height: isMobile || isTablet ? 450 : isSmall ? 250 : height * 0.75,
        },
      ]}
    >
      {loading ? (
        <Text style={styles.message}>Loading tournaments...</Text>
      ) : error ? (
        <Text style={styles.message}>{error}</Text>
      ) : pages.length === 0 ? (
        <Text style={styles.message}>No active tournaments</Text>
      ) : (
        <>
          <View
            style={{ justifyContent: "center", marginTop: isSmall ? 0 : 40 }}
          >
            <Carousel
              ref={carouselRef}
              loop={pages.length > 1}
              width={isSmall ? width * 0.7 : width}
              height={
                isMobile || isTablet ? 450 : isSmall ? 250 : height * 0.75
              }
              autoPlay={false}
              data={pages}
              scrollAnimationDuration={1200}
              renderItem={({ item: page, index: pageIndex }) => (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    width: isPortrait ? width : width * 0.65,
                    paddingHorizontal: 8,
                    marginHorizontal: "auto",
                  }}
                >
                  {page.map((room, itemIndex) => {
                    const globalIndex = pageIndex * itemsPerPage + itemIndex;

                    const cardWidth = isMobile
                      ? width - 32
                      : isSmall
                        ? width * 0.2
                        : width * 0.2;

                    return (
                      <View
                        key={room.id ?? globalIndex}
                        style={{
                          width: cardWidth,
                          marginHorizontal: 8,
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: isXLg
                            ? height * 0.7
                            : isLg
                              ? height * 0.6
                              : isMobile
                                ? 450
                                : isSmall
                                  ? 200
                                  : height * 0.6,
                        }}
                      >
                        <TournamentTable
                          key={room.id ?? itemIndex}
                          room={room}
                          totalCount={tournaments.length}
                          itemIndex={globalIndex}
                        />
                      </View>
                    );
                  })}
                </View>
              )}
            />
          </View>

          {pages.length > 1 && (
            <>
              <TouchableOpacity style={styles.arrowLeft} onPress={handlePrev}>
                <ImageBackground
                  source={LeftArrowPng}
                  resizeMode="cover"
                  style={styles.arrowBg}
                  imageStyle={styles.arrowImage}
                />
              </TouchableOpacity>

              <TouchableOpacity style={styles.arrowRight} onPress={handleNext}>
                <ImageBackground
                  source={RightArrowPng}
                  resizeMode="cover"
                  style={styles.arrowBg}
                  imageStyle={styles.arrowImage}
                />
              </TouchableOpacity>
            </>
          )}
        </>
      )}
    </View>
  );
};

export default ActiveTournamentsSection;

const styles = StyleSheet.create({
  container: {
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  arrowLeft: {
    position: "absolute",
    top: "45%",
    left: 10,
    borderRadius: 20,
    zIndex: 10,
  },
  arrowRight: {
    position: "absolute",
    top: "45%",
    right: 10,
    borderRadius: 20,
    zIndex: 10,
  },
  arrowBg: {
    width: 50,
    height: 50,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 20,
    padding: 10,
  },
  arrowImage: {
    borderRadius: 20,
  },
  message: {
    color: "#fff",
    fontSize: 20,
    textAlign: "center",
  },
});
